import { useEffect, useMemo, useState, type ReactElement } from "react";
import { RotateCcw, Save, Trash2, Unlink } from "lucide-react";
import { Badge, Button, FormField, Select, Stack, Surface, Text, TextInput } from "@ux/primitives";
import { BottomActionBar, Toolbar } from "@ux/compositions";
import { useViewport } from "@ux/hooks";
import { palette, space } from "@ux/tokens";
import { useCanEdit } from "@shell/auth/useCanEdit";
import { formatActor, formatTimestamp } from "../common/formatting";
import type { AssetAssignmentView } from "../asset-assignments/types";
import { AssignTargetDialog, type AssignTargetDialogMode } from "../assets/AssignTargetDialog";
import type { AssetAssignTarget } from "../assets/types";
import type { SageOfficeView } from "../sage-offices/types";
import { useUserDirectoryEntry } from "../users/hooks";
import { formatUserDisplay } from "../users/display";
import { useWorkspaceAssignments } from "../workspace-assignments/hooks";
import { workspaceTypes, type WorkspaceUpsertPayload, type WorkspaceView } from "./types";
import { WorkspaceAssetsPanel } from "./WorkspaceAssetsPanel";

export type WorkspaceFormMode =
    | { kind: "create"; defaultSageOfficeId: string | null }
    | { kind: "edit"; record: WorkspaceView };

export interface WorkspaceFormProps {
    readonly mode: WorkspaceFormMode;
    readonly offices: readonly SageOfficeView[];
    readonly onSave: (payload: WorkspaceUpsertPayload) => void;
    readonly onCancel: () => void;
    readonly onRequestDelete: (id: string) => void;
    readonly onRequestRestore: (id: string) => void;
    /** Edit-mode only: emit when the user wants to add a new asset to this workspace. */
    readonly onRequestAddAsset?: (workspaceId: string) => void;
    /** Edit-mode only: emit when the user wants to pick an existing asset to (re)assign here. */
    readonly onRequestAssignExisting?: (workspaceId: string) => void;
    /** Edit-mode only: emit when the user wants to release an open assignment from this workspace. */
    readonly onRequestReleaseAssignment?: (assignment: AssetAssignmentView) => void;
    /** Edit-mode only: emit when the user wants to walk the workspace in inventory mode. */
    readonly onRequestStartInventory?: (workspaceId: string) => void;
    /** Edit-mode only: emit when the user wants to move all of this workspace's assets to another workspace. */
    readonly onRequestReassignAll?: (workspaceId: string) => void;
    /**
     * Edit-mode only. Fired when the user picks a user in the
     * occupant Assign / Re-assign dialog. Single callback for both
     * flavors because the workspace-assignment endpoint is atomic —
     * if a previous occupant exists it's released inside the same
     * Cosmos batch as the new assignment, so callers don't need to
     * orchestrate release-then-create.
     */
    readonly onRequestAssignOccupant?: (userId: string) => void;
    /**
     * Edit-mode only. Fired when the user picks "Release" on an
     * occupied workspace. Parent should release the supplied assignment.
     */
    readonly onRequestReleaseOccupant?: (currentAssignmentId: string) => void;
    /** Disables Assign / Re-assign / Release buttons while a related mutation is in-flight. */
    readonly isOccupantBusy?: boolean;
    /** Surfaced inside the occupant section if the latest assign/release failed. */
    readonly occupantErrorMessage?: string | null;
    readonly isSaving: boolean;
    readonly errorMessage: string | null;
}

interface FormState {
    readonly id: string;
    readonly sageOfficeId: string;
    readonly type: string;
    readonly name: string;
    /**
     * Always a `string` in form state (controlled inputs choke on
     * `null`). It's normalized to `null` when missing on the way *in*
     * and back to `null` when blank on the way *out* — see
     * `initialState` and `handleSubmit`.
     */
    readonly friendlyName: string;
    readonly floorplanElementId: string;
}

export function WorkspaceForm({
    mode,
    offices,
    onSave,
    onCancel,
    onRequestDelete,
    onRequestRestore,
    onRequestAddAsset,
    onRequestAssignExisting,
    onRequestReleaseAssignment,
    onRequestStartInventory,
    onRequestReassignAll,
    onRequestAssignOccupant,
    onRequestReleaseOccupant,
    isOccupantBusy = false,
    occupantErrorMessage,
    isSaving,
    errorMessage,
}: WorkspaceFormProps): ReactElement {
    const { isPhone } = useViewport();
    const [state, setState] = useState<FormState>(() => initialState(mode));
    const [validationError, setValidationError] = useState<string | null>(null);

    useEffect(() => {
        setState(initialState(mode));
        setValidationError(null);
    }, [mode]);

    function handleSubmit(): void {
        // Empty/whitespace friendly name → null on the wire, matching
        // the backend's normalization. Keeps "absent" as the single
        // representable state for "no friendly name."
        const trimmedFriendlyName = state.friendlyName.trim();
        // Map placement uses workspace name (= SVG group id). Keep any
        // legacy floorplanElementId as-is; do not derive it from name.
        const existingFloorplanElementId =
            mode.kind === "edit"
                ? (mode.record.floorplanElementId?.trim() || null)
                : null;
        const payload: WorkspaceUpsertPayload = {
            id: state.id,
            sageOfficeId: state.sageOfficeId,
            type: state.type,
            name: state.name.trim(),
            friendlyName: trimmedFriendlyName === "" ? null : trimmedFriendlyName,
            floorplanElementId: existingFloorplanElementId,
        };
        const error = validate(payload);
        if (error !== null) {
            setValidationError(error);
            return;
        }
        setValidationError(null);
        onSave(payload);
    }

    const isDeletedRecord = mode.kind === "edit" && mode.record.isDeleted;
    const canEdit = useCanEdit();
    const isReadOnly = isDeletedRecord || !canEdit;
    const auditCreated = mode.kind === "edit" ? mode.record.created : null;
    const auditModified = mode.kind === "edit" ? mode.record.modified : null;
    const auditDeleted = mode.kind === "edit" ? mode.record.deleted : null;

    const officeOptions = offices.map((o) => ({
        value: o.id,
        label: o.isDeleted ? `${o.name} (deleted)` : o.name,
    }));
    const typeOptions = workspaceTypes.map((t) => ({ value: t, label: t }));

    const actionButtons = (
        <>
            {/*
                "Inventory mode" used to live here. It's now rendered
                inside `WorkspaceAssetsPanel` next to "Add new asset" /
                "Assign existing" — that's where it semantically belongs
                (bulk asset capture, not a form-state action), and it
                un-crowds the phone BottomActionBar so Save/Delete/
                Cancel are no longer competing with a fourth button for
                thumb-zone real estate.
            */}
            <Button variant="ghost" onClick={onCancel} disabled={isSaving}>
                Cancel
            </Button>
            {canEdit && mode.kind === "edit" && isDeletedRecord ? (
                <Button
                    variant="secondary"
                    leadingIcon={<RotateCcw size={16} />}
                    onClick={() => onRequestRestore(mode.record.id)}
                    disabled={isSaving}
                >
                    Restore
                </Button>
            ) : null}
            {canEdit && mode.kind === "edit" && !isDeletedRecord ? (
                <Button
                    variant="danger"
                    leadingIcon={<Trash2 size={16} />}
                    onClick={() => onRequestDelete(mode.record.id)}
                    disabled={isSaving}
                >
                    Delete
                </Button>
            ) : null}
            {canEdit ? (
                <Button
                    variant="primary"
                    leadingIcon={<Save size={16} />}
                    onClick={handleSubmit}
                    disabled={isSaving || isDeletedRecord}
                >
                    {isSaving ? "Saving…" : "Save"}
                </Button>
            ) : null}
        </>
    );

    return (
        <Stack direction="column" gap={0} grow>
            <Toolbar
                leading={
                    <Stack direction="row" gap={2} align="center">
                        <Text role="heading">
                            {mode.kind === "create" ? "New workspace" : mode.record.name}
                        </Text>
                        {isDeletedRecord ? <Badge tone="danger">Deleted</Badge> : null}
                    </Stack>
                }
                trailing={
                    isPhone ? null : (
                        <Stack direction="row" gap={2} wrap>
                            {actionButtons}
                        </Stack>
                    )
                }
            />

            <div style={{ flex: 1, overflow: "auto", padding: space[5] }}>
                <Stack gap={4} style={{ maxWidth: 640 }}>
                    {validationError !== null ? (
                        <Surface tone="raised" padding={3}>
                            <Text role="muted" color={palette.danger}>
                                {validationError}
                            </Text>
                        </Surface>
                    ) : null}
                    {errorMessage !== null ? (
                        <Surface tone="raised" padding={3}>
                            <Text role="muted" color={palette.danger}>
                                {errorMessage}
                            </Text>
                        </Surface>
                    ) : null}

                    {mode.kind === "edit" ? (
                        <FormField
                            label="Workspace ID"
                            hint="Stable id for this workspace. Assignment can also use the name or number."
                        >
                            {(id) => (
                                <TextInput id={id} value={mode.record.id} readOnly disabled />
                            )}
                        </FormField>
                    ) : null}

                    <FormField label="Sage office" required>
                        {(id) => (
                            <Select
                                id={id}
                                value={state.sageOfficeId}
                                onChange={(e) =>
                                    setState((p) => ({ ...p, sageOfficeId: e.target.value }))
                                }
                                options={officeOptions}
                                placeholder="Select an office"
                                disabled={isReadOnly}
                            />
                        )}
                    </FormField>

                    <FormField label="Type" required>
                        {(id) => (
                            <Select
                                id={id}
                                value={state.type}
                                onChange={(e) => setState((p) => ({ ...p, type: e.target.value }))}
                                options={typeOptions}
                                placeholder="Select a type"
                                disabled={isReadOnly}
                            />
                        )}
                    </FormField>

                    <FormField
                        label="Name"
                        required
                        hint="Must match the SVG group id on the floorplan (e.g. 114 or Desk001)."
                    >
                        {(id) => (
                            <TextInput
                                id={id}
                                value={state.name}
                                onChange={(e) => setState((p) => ({ ...p, name: e.target.value }))}
                                disabled={isReadOnly}
                                placeholder="e.g. 114"
                            />
                        )}
                    </FormField>

                    <FormField
                        label="Friendly name"
                        hint="Optional alias. e.g. 'I.T. Collaboration Room'. Searchable."
                    >
                        {(id) => (
                            <TextInput
                                id={id}
                                value={state.friendlyName}
                                onChange={(e) =>
                                    setState((p) => ({ ...p, friendlyName: e.target.value }))
                                }
                                disabled={isReadOnly}
                                placeholder="e.g. I.T. Collaboration Room"
                            />
                        )}
                    </FormField>

                    <FormField
                        label="Floorplan element id"
                        hint="Legacy field. The map matches workspace name to the SVG group id."
                    >
                        {(id) => (
                            <TextInput
                                id={id}
                                value={state.floorplanElementId}
                                readOnly
                                disabled
                                placeholder="—"
                            />
                        )}
                    </FormField>

                    {canEdit && mode.kind === "edit" && !isDeletedRecord ? (
                        <OccupantSection
                            workspace={mode.record}
                            onAssign={onRequestAssignOccupant}
                            onRelease={onRequestReleaseOccupant}
                            isBusy={isOccupantBusy}
                            errorMessage={occupantErrorMessage ?? null}
                        />
                    ) : null}

                    {canEdit &&
                    mode.kind === "edit" &&
                    onRequestAddAsset !== undefined &&
                    onRequestAssignExisting !== undefined &&
                    onRequestReleaseAssignment !== undefined ? (
                        <WorkspaceAssetsPanel
                            workspaceId={mode.record.id}
                            onAddNewAsset={() => onRequestAddAsset(mode.record.id)}
                            onAssignExistingAsset={() => onRequestAssignExisting(mode.record.id)}
                            onReleaseAssignment={onRequestReleaseAssignment}
                            {...(!isDeletedRecord && onRequestStartInventory !== undefined
                                ? { onStartInventory: () => onRequestStartInventory(mode.record.id) }
                                : {})}
                            {...(!isDeletedRecord && onRequestReassignAll !== undefined
                                ? { onReassignAll: () => onRequestReassignAll(mode.record.id) }
                                : {})}
                            disabled={isDeletedRecord}
                        />
                    ) : null}

                    {mode.kind === "edit" ? (
                        <Surface tone="raised" padding={4}>
                            <Stack gap={2}>
                                <Text role="eyebrow">Audit</Text>
                                <Text role="muted">Created — {formatActor(auditCreated)}</Text>
                                <Text role="muted">Modified — {formatActor(auditModified)}</Text>
                                {auditDeleted ? (
                                    <Text role="muted" color={palette.danger}>
                                        Deleted — {formatActor(auditDeleted)}
                                    </Text>
                                ) : null}
                            </Stack>
                        </Surface>
                    ) : null}
                </Stack>
            </div>

            {isPhone ? <BottomActionBar>{actionButtons}</BottomActionBar> : null}
        </Stack>
    );
}

function initialState(mode: WorkspaceFormMode): FormState {
    if (mode.kind === "create") {
        return {
            id: crypto.randomUUID(),
            sageOfficeId: mode.defaultSageOfficeId ?? "",
            type: "",
            name: "",
            friendlyName: "",
            floorplanElementId: "",
        };
    }
    return {
        id: mode.record.id,
        sageOfficeId: mode.record.sageOfficeId,
        type: mode.record.type,
        name: mode.record.name,
        // Existing record may have null or undefined here for
        // workspaces created before the field existed. Either way the
        // input renders empty.
        friendlyName: mode.record.friendlyName ?? "",
        floorplanElementId: mode.record.floorplanElementId ?? "",
    };
}

function validate(payload: WorkspaceUpsertPayload): string | null {
    if (payload.sageOfficeId === "") return "Pick the sage office this workspace belongs to.";
    if (payload.type === "") return "Pick a workspace type.";
    if (payload.name === "") return "Name is required.";
    return null;
}

interface OccupantSectionProps {
    readonly workspace: WorkspaceView;
    // Widened to `| undefined` (rather than `?`) so the parent can pass
    // its own optional callbacks through without conditional-spread
    // gymnastics. exactOptionalPropertyTypes treats `?` as "key may be
    // omitted" while `| undefined` is "value may be undefined".
    readonly onAssign: ((userId: string) => void) | undefined;
    readonly onRelease: ((currentAssignmentId: string) => void) | undefined;
    readonly isBusy: boolean;
    readonly errorMessage: string | null;
}

/**
 * Edit-mode-only section that lives between the workspace's identity
 * fields and its asset panel. Shows the current occupant (if any) and
 * provides the same Assign / Re-assign / Release controls we offer for
 * asset assignments, but for the workspace ↔ user relationship.
 *
 * Two states:
 *   - **Vacant** (no open assignment): single "Assign user…" CTA opens
 *     the shared AssignTargetDialog locked to "User".
 *   - **Occupied** (open assignment exists): shows the friendly user
 *     name + since-date, with "Re-assign to…" and "Release" actions.
 *
 * Backend semantics simplify the re-assign flow vs. the asset side:
 * `POST /api/workspace-assignments` releases the previous occupant and
 * creates the new assignment in a single Cosmos batch (one open
 * assignment per workspace is the invariant). So the parent only needs
 * one mutation to hand to `onAssign` regardless of whether the
 * workspace was vacant or occupied — there's no release-then-create
 * sequence to orchestrate.
 */
function OccupantSection({
    workspace,
    onAssign,
    onRelease,
    isBusy,
    errorMessage,
}: OccupantSectionProps): ReactElement | null {
    const canAssign = onAssign !== undefined;
    const canRelease = onRelease !== undefined;

    // All hook calls must precede the early-return below — otherwise
    // unmounting a parent that toggles capability props mid-flight would
    // change hook order across renders.
    const [dialog, setDialog] = useState<
        | { kind: "closed" }
        | { kind: "assign" }
        | { kind: "reassign"; currentAssignmentId: string; currentOccupantLabel: string }
    >({ kind: "closed" });

    // Resolve the current open occupant (at most one, by invariant).
    const occupantsQuery = useWorkspaceAssignments({
        workspaceId: workspace.id,
        userId: "",
        openOnly: true,
    });
    const currentOccupant = occupantsQuery.data?.[0] ?? null;

    const occupantUserId = currentOccupant?.userId ?? null;
    const occupantEntry = useUserDirectoryEntry(occupantUserId);

    const occupantLabel = useMemo(() => {
        if (occupantUserId === null) return "—";
        return formatUserDisplay(occupantEntry.data ?? null, occupantUserId);
    }, [occupantUserId, occupantEntry.data]);

    if (!canAssign && !canRelease) {
        // Nothing the user can do here — render nothing rather than a
        // useless read-only block.
        return null;
    }

    function handleSubmit(target: AssetAssignTarget): void {
        // The dialog is locked to "User", so target.kind is always "User"
        // here — but the type guard keeps TypeScript happy and prevents
        // accidental misuse if the lock is ever removed.
        if (target.kind !== "User") return;
        if (onAssign === undefined) return;
        onAssign(target.id);
        setDialog({ kind: "closed" });
    }

    const dialogProps =
        dialog.kind === "assign"
            ? {
                  open: true,
                  mode: { kind: "assign" } as AssignTargetDialogMode,
              }
            : dialog.kind === "reassign"
              ? {
                    open: true,
                    mode: {
                        kind: "reassign" as const,
                        currentTargetLabel: dialog.currentOccupantLabel,
                    } as AssignTargetDialogMode,
                }
              : null;

    return (
        <Surface tone="raised" padding={3}>
            <Stack gap={2}>
                <Stack direction="row" align="center" justify="space-between" gap={2} wrap>
                    <Stack gap={0}>
                        <Text role="eyebrow">Occupant</Text>
                        {occupantsQuery.isPending ? (
                            <Text role="muted">Loading…</Text>
                        ) : currentOccupant === null ? (
                            <Text role="body">No current occupant</Text>
                        ) : (
                            <Stack gap={0}>
                                <Text role="body">Occupied by {occupantLabel}</Text>
                                <Text role="muted">User ID {occupantUserId}</Text>
                                <Text role="muted">
                                    Since {formatTimestamp(currentOccupant.assigned.timeStamp)}
                                </Text>
                            </Stack>
                        )}
                    </Stack>
                    <Stack direction="row" gap={2} wrap>
                        {currentOccupant === null ? (
                            canAssign ? (
                                <Button
                                    variant="primary"
                                    size="sm"
                                    onClick={() => setDialog({ kind: "assign" })}
                                    disabled={isBusy}
                                >
                                    Assign user…
                                </Button>
                            ) : null
                        ) : (
                            <>
                                {canAssign ? (
                                    <Button
                                        variant="primary"
                                        size="sm"
                                        onClick={() =>
                                            setDialog({
                                                kind: "reassign",
                                                currentAssignmentId: currentOccupant.id,
                                                currentOccupantLabel: occupantLabel,
                                            })
                                        }
                                        disabled={isBusy}
                                    >
                                        Re-assign to…
                                    </Button>
                                ) : null}
                                {canRelease ? (
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        leadingIcon={<Unlink size={14} />}
                                        onClick={() => onRelease(currentOccupant.id)}
                                        disabled={isBusy}
                                    >
                                        Release
                                    </Button>
                                ) : null}
                            </>
                        )}
                    </Stack>
                </Stack>
                {errorMessage !== null ? (
                    <Text role="muted" color={palette.danger}>
                        {errorMessage}
                    </Text>
                ) : null}
            </Stack>

            {dialogProps !== null ? (
                <AssignTargetDialog
                    open={dialogProps.open}
                    mode={dialogProps.mode}
                    // defaultKind is required by the dialog's contract,
                    // but lockedKind takes precedence so this value is
                    // effectively ignored. We pass "User" both for
                    // documentation and as a sane fallback.
                    defaultKind="User"
                    lockedKind="User"
                    assetLabel={`${workspace.name} · ${workspace.type}`}
                    onSubmit={handleSubmit}
                    onCancel={() => setDialog({ kind: "closed" })}
                    isWorking={isBusy}
                />
            ) : null}
        </Surface>
    );
}
